import Box from "@mui/material/Box"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import Typography from "@mui/material/Typography"
import { useTheme } from "@mui/material/styles"
import { Editor } from "@tinymce/tinymce-react"
import { useEffect, useRef, useState } from "react"

import InsertLinkDialog from "../components/InsertLinkDialog"
import {
  CAMPAIGN_SCRIPT_COMPONENTS,
  CAMPAIGN_SCRIPT_EDITOR_CONTENT_STYLE,
  CAMPAIGN_SCRIPT_PLACEHOLDERS,
  applyVisualBodyToSource,
  createStyleProtectRe,
  extractBodyHtml,
  extractEmbeddedCss,
  normalizePastedCampaignScriptHtml,
  prepareCampaignScriptContent,
  shouldTreatPasteAsHtml,
  wrapPlaceholder,
} from "./campaignScriptConstants"

export default function CampaignScriptEditor({
  content,
  onChange,
  disabled = false,
  height = 520,
}) {
  const theme = useTheme()
  const editorRef = useRef(null)
  const sourceRef = useRef(content || "")
  const [editorReady, setEditorReady] = useState(false)
  const [viewMode, setViewMode] = useState("visual")
  const [insertLinkDialogOpen, setInsertLinkDialogOpen] = useState(false)
  const lastPushedHtml = useRef(null)

  useEffect(() => {
    sourceRef.current = content || ""
  }, [content])

  useEffect(() => {
    const prepared = prepareCampaignScriptContent(content)
    if (prepared !== content) {
      onChange(prepared)
    }
  }, [content, onChange])

  useEffect(() => {
    if (viewMode !== "visual") {
      setEditorReady(false)
    }
  }, [viewMode])

  useEffect(() => {
    if (viewMode !== "visual" || !editorReady || !editorRef.current) {
      return
    }
    const visualHtml = extractBodyHtml(content || "")
    const current = editorRef.current.getContent() || ""
    if (current === visualHtml) {
      return
    }
    lastPushedHtml.current = visualHtml
    editorRef.current.setContent(visualHtml)
  }, [content, editorReady, viewMode])

  function handleViewChange(_event, nextMode) {
    if (!nextMode || nextMode === viewMode) {
      return
    }
    if (nextMode === "html" && editorRef.current) {
      const visualBody = editorRef.current.getContent() || ""
      const merged = applyVisualBodyToSource(sourceRef.current, visualBody)
      if (merged !== content) {
        onChange(merged)
      }
    }
    setViewMode(nextMode)
  }

  const embeddedCss = extractEmbeddedCss(content)

  function importPastedHtml(rawHtml) {
    const normalized = normalizePastedCampaignScriptHtml(rawHtml)
    const visualBody = extractBodyHtml(normalized)
    sourceRef.current = normalized
    if (viewMode === "visual" && editorRef.current) {
      lastPushedHtml.current = visualBody
      editorRef.current.setContent(visualBody)
    }
    onChange(normalized)
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {viewMode === "visual" ? "Visual editor" : "HTML editor"}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={viewMode}
          onChange={handleViewChange}
          disabled={disabled}
          sx={{
            bgcolor: "background.paper",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              px: 1.75,
              py: 0.5,
              fontWeight: 600,
              borderColor: theme.palette.divider,
            },
          }}
        >
          <ToggleButton value="visual">Visual</ToggleButton>
          <ToggleButton value="html">HTML</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === "visual" ? (
        <Box
          sx={{
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
            "& .tox-tinymce": { border: "none !important" },
          }}
        >
          <Editor
            key="campaign-script-visual"
            apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
            disabled={disabled}
            onInit={(_evt, editor) => {
              editorRef.current = editor
            }}
            onEditorChange={(html) => {
              if (lastPushedHtml.current === html) {
                lastPushedHtml.current = null
                return
              }
              onChange(applyVisualBodyToSource(sourceRef.current, html))
            }}
            init={{
              height,
              menubar: false,
              statusbar: false,
              branding: false,
              plugins: "lists link table autolink",
              toolbar: [
                "undo redo | styles | bold italic underline strikethrough | forecolor backcolor",
                "alignleft aligncenter alignright alignjustify | bullist numlist | table hr | insert-link | insert-component | insert-placeholder",
              ],
              content_style: `${CAMPAIGN_SCRIPT_EDITOR_CONTENT_STYLE}\n${embeddedCss}`,
              relative_urls: false,
              convert_urls: false,
              verify_html: false,
              valid_elements: "*[*]",
              invalid_elements: "script,iframe,object,embed,link,meta",
              extended_valid_elements:
                "style[type],div[*],span[*],p[*],table[*],td[*],th[*],a[*],font[*],input[*],label[*]",
              protect: [createStyleProtectRe()],
              setup: (editor) => {
                editor.on("init", () => {
                  setEditorReady(true)
                  editor.setContent(extractBodyHtml(content || ""))
                })
                editor.on("paste", (event) => {
                  const clipboardData = event.clipboardData
                  if (!clipboardData) {
                    return
                  }
                  const plain = clipboardData.getData("text/plain")
                  if (!shouldTreatPasteAsHtml(plain)) {
                    return
                  }
                  event.preventDefault()
                  importPastedHtml(plain)
                })
                editor.on("PastePreProcess", (event) => {
                  if (!shouldTreatPasteAsHtml(event.content)) {
                    return
                  }
                  event.content = extractBodyHtml(
                    normalizePastedCampaignScriptHtml(event.content)
                  )
                })
                editor.ui.registry.addButton("insert-link", {
                  tooltip: "Insert link",
                  icon: "link",
                  onAction: () => setInsertLinkDialogOpen(true),
                })
                editor.ui.registry.addMenuButton("insert-component", {
                  text: "Components",
                  fetch: (callback) => {
                    callback(
                      CAMPAIGN_SCRIPT_COMPONENTS.map((component) => ({
                        type: "menuitem",
                        text: component.label,
                        onAction: () => editor.insertContent(component.html),
                      }))
                    )
                  },
                })
                editor.ui.registry.addMenuButton("insert-placeholder", {
                  text: "Placeholder",
                  fetch: (callback) => {
                    callback(
                      CAMPAIGN_SCRIPT_PLACEHOLDERS.map((option) => ({
                        type: "menuitem",
                        text: option.label,
                        onAction: () =>
                          editor.insertContent(wrapPlaceholder(option.token)),
                      }))
                    )
                  },
                })
              },
            }}
          />
        </Box>
      ) : null}

      {viewMode === "html" ? (
        <Box
          component="textarea"
          aria-label="Campaign script HTML source"
          disabled={disabled}
          value={content}
          spellCheck={false}
          placeholder="Paste or edit the campaign script HTML here…"
          onChange={(event) => onChange(event.target.value)}
          onPaste={(event) => {
            const plain = event.clipboardData.getData("text/plain")
            if (!shouldTreatPasteAsHtml(plain)) {
              return
            }
            event.preventDefault()
            importPastedHtml(plain)
          }}
          sx={{
            display: "block",
            width: "100%",
            minHeight: height,
            boxSizing: "border-box",
            resize: "vertical",
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            p: 2,
            m: 0,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 13,
            lineHeight: 1.5,
            whiteSpace: "pre-wrap",
            overflow: "auto",
            tabSize: 2,
            color: "text.primary",
            bgcolor: "background.paper",
            outline: "none",
            "&:focus": {
              borderColor:
                theme.palette.colors?.brand500 ?? theme.palette.primary.main,
            },
          }}
        />
      ) : null}

      <InsertLinkDialog
        open={insertLinkDialogOpen}
        onClose={() => setInsertLinkDialogOpen(false)}
        editorRef={editorRef}
      />
    </Box>
  )
}
